/**
 * Tracks whether the API server is reachable.
 *
 * Distinct from `OfflineBanner` (which watches `navigator.onLine` — i.e.
 * "the browser has no internet"). Here we track the case where the
 * browser is fine but our own API is down: `fetch` returns
 * `ERR_CONNECTION_REFUSED`, RTK Query maps that to
 * `error.status === 'FETCH_ERROR'`.
 *
 * Wired by `baseQuery`:
 * - `markUnreachable()` fires when a FETCH_ERROR lands AND `navigator.onLine`
 *   is true (so genuine "user is offline" doesn't false-positive into this
 *   state — that's the offline banner's job).
 * - `markReachable()` fires whenever any request comes back successfully,
 *   so the moment the API recovers the overlay lifts on its own.
 *
 * Consumed by `ApiUnreachablePage`, which renders a full-screen "we can't
 * reach our servers" page over the rest of the app while `unreachable=true`.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface ApiStatusState {
  unreachable: boolean;
  // Epoch ms of the first failed request in the current outage window.
  // Cleared as soon as a request succeeds again.
  since: number | null;
  // Count of consecutive failed requests. Used by the overlay to decide
  // whether to show a "still trying" hint after a couple of seconds.
  consecutiveFailures: number;
}

const initialState: ApiStatusState = {
  unreachable: false,
  since: null,
  consecutiveFailures: 0,
};

export const apiStatusSlice = createSlice({
  name: 'apiStatus',
  initialState,
  reducers: {
    markUnreachable(state, action: PayloadAction<{ at?: number } | undefined>) {
      const at = action.payload?.at ?? Date.now();
      if (!state.unreachable) {
        state.unreachable = true;
        state.since = at;
      }
      state.consecutiveFailures += 1;
    },
    markReachable(state) {
      if (state.unreachable || state.consecutiveFailures > 0) {
        state.unreachable = false;
        state.since = null;
        state.consecutiveFailures = 0;
      }
    },
  },
});

export const { markUnreachable, markReachable } = apiStatusSlice.actions;
export const apiStatusReducer = apiStatusSlice.reducer;
export type { ApiStatusState };
